import { type FormEvent, useState } from "react";

import { api } from "../lib/api";
import { useLang } from "../lib/i18n";
import { authCopy } from "../lib/i18n/auth";
import { ScreenShell } from "./screen-shell";
import { IconBack } from "./ui/icon";
import { Eyebrow, FormField, H1, Input, MonoMeta, PrimaryButton } from "./ui/primitives";
import { SytoLogo } from "./ui/syto-logo";

// Forgot-password ("request reset link"), follows B_ResetPwd in
// design_handoff_sytofood/screens-b2.jsx (~line 271-310): logo, back-link,
// editorial display heading, single Email field, tangerine CTA, surface-2
// hint card. Success state mirrors the design's terminal-style success copy.

type Props = {
  onBack: () => void;
};

export function ForgotPasswordScreen({ onBack }: Props) {
  const { lang } = useLang();
  const t = authCopy[lang];
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await api.requestPasswordReset(email);
      setSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : t.genericError);
    } finally {
      setSubmitting(false);
    }
  }

  if (sent) {
    return (
      <ScreenShell hideHeader>
        <div style={{ marginBottom: 20 }}>
          <SytoLogo size={22} />
        </div>

        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-1.5 text-[14px] font-semibold"
          style={{
            color: "var(--ink-2)",
            marginTop: 18,
            marginBottom: 32,
            alignSelf: "flex-start",
          }}
        >
          <IconBack size={20} />
          <span>{t.fpBackToSignIn}</span>
        </button>

        <Eyebrow>{t.fpSentEyebrow}</Eyebrow>
        <H1 accent="." className="mt-3">
          {t.fpSentHeading}
        </H1>
        <p
          style={{
            marginTop: 14,
            fontSize: 14,
            color: "var(--ink-2)",
            lineHeight: 1.5,
          }}
        >
          {t.fpSentBodyPrefix}
          <strong style={{ color: "var(--ink)" }}>{email}</strong>
          {t.fpSentBodySuffix}
        </p>

        <div
          style={{
            marginTop: 18,
            padding: "12px 14px",
            borderRadius: 10,
            background: "var(--surface-2)",
          }}
        >
          <MonoMeta>{t.fpHintLabel}</MonoMeta>
          <div style={{ marginTop: 6, fontSize: 12, color: "var(--ink-2)" }}>
            {t.fpHintBody}
          </div>
        </div>

        <PrimaryButton onClick={onBack} className="mt-6">
          {t.fpBackButton}
        </PrimaryButton>
      </ScreenShell>
    );
  }

  return (
    <ScreenShell hideHeader>
      <div style={{ marginBottom: 20 }}>
        <SytoLogo size={22} />
      </div>

      <button
        type="button"
        onClick={onBack}
        className="inline-flex items-center gap-1.5 text-[14px] font-semibold"
        style={{ color: "var(--ink-2)", marginTop: 18, marginBottom: 32, alignSelf: "flex-start" }}
      >
        <IconBack size={20} />
        <span>{t.fpBackToSignIn}</span>
      </button>

      <Eyebrow>{t.fpEyebrow}</Eyebrow>
      <H1 accent="." className="mt-3">
        {t.fpHeading}
      </H1>
      <p
        style={{
          marginTop: 14,
          fontSize: 14,
          color: "var(--ink-2)",
          lineHeight: 1.5,
        }}
      >
        {t.fpIntro}
      </p>

      <form onSubmit={handleSubmit} className="flex flex-col gap-3" style={{ marginTop: 24 }}>
        <FormField label={t.emailLabel}>
          <Input
            type="email"
            required
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t.emailPlaceholder}
          />
        </FormField>

        {error ? (
          <p
            className="text-[12px] px-3 py-2 rounded-md"
            style={{ background: "var(--bad-bg)", color: "var(--bad-fg)" }}
          >
            {error}
          </p>
        ) : null}

        <PrimaryButton type="submit" disabled={submitting || !email.trim()} className="mt-2">
          {submitting ? t.fpSubmitting : t.fpSubmit}
        </PrimaryButton>
      </form>

      <div
        style={{
          marginTop: 18,
          padding: "12px 14px",
          borderRadius: 10,
          background: "var(--surface-2)",
        }}
      >
        <MonoMeta>{t.fpHintLabel}</MonoMeta>
        <div style={{ marginTop: 6, fontSize: 12, color: "var(--ink-2)" }}>
          {t.fpHintBody}
        </div>
      </div>
    </ScreenShell>
  );
}
